import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AdminUser, AuditLogEntry, PlatformRole } from "@edufadder/shared-types";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";
import { AdminInsightsView } from "../components/AdminInsights.js";

const ROLES: PlatformRole[] = ["user", "researcher", "reviewer", "system_admin"];

/** While self-service sign-up is closed, verified people get their account here. */
function AddVerifiedAccount({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<PlatformRole>("user");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setCreated(null);
    try {
      const r = await api.createVerifiedAccount({ email, display_name: name, role });
      setCreated({ email: r.user.email, password: r.temporary_password });
      setEmail("");
      setName("");
      setRole("user");
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create that account.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ ...s.card, marginBottom: 16 }} aria-labelledby="add-account">
      <h2 id="add-account" style={s.heading2}>Add a verified account</h2>
      <p style={s.mutedText}>
        Sign-up on the website is closed; people are asked to contact the team for verification. Once you've verified
        someone, create their account here. You'll get a one-time temporary password to pass on privately — they should
        change it under <em>Account → Change password</em> after signing in.
      </p>
      <form onSubmit={(e) => void submit(e)} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ ...s.label, flex: "1 1 200px" }}>
          Name
          <input style={s.input} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label style={{ ...s.label, flex: "1 1 240px" }}>
          Email
          <input style={s.input} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label style={{ ...s.label, flex: "0 0 160px" }}>
          Role
          <select style={s.input} value={role} onChange={(e) => setRole(e.target.value as PlatformRole)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" style={{ ...s.buttonPrimary, marginBottom: 12 }} disabled={busy}>
          {busy ? "Creating…" : "Create account"}
        </button>
      </form>
      {error && <p style={s.errorText}>{error}</p>}
      {created && (
        <div role="status" style={{ ...s.card, background: "#faf3e2", marginTop: 8 }}>
          Account created for <strong>{created.email}</strong>. Temporary password (shown only now):{" "}
          <code style={{ fontSize: 15, userSelect: "all" }}>{created.password}</code>
        </div>
      )}
    </section>
  );
}

function UsersTab(): JSX.Element {
  const { me } = useAuth();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load(): void {
    api
      .listUsers()
      .then(setUsers)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load users."));
  }
  useEffect(load, []);

  async function changeRole(userId: string, role: PlatformRole): Promise<void> {
    setError(null);
    try {
      await api.patchUser(userId, { role });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't update that user's role.");
    }
  }

  async function toggleActive(userId: string, isActive: boolean): Promise<void> {
    setError(null);
    try {
      await api.patchUser(userId, { is_active: !isActive });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't update that user.");
    }
  }

  return (
    <div>
      <AddVerifiedAccount onCreated={load} />
      {error && <p style={s.errorText}>{error}</p>}
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Name</th>
            <th style={s.th}>Email</th>
            <th style={s.th}>User ID</th>
            <th style={s.th}>Role</th>
            <th style={s.th}>Active</th>
          </tr>
        </thead>
        <tbody>
          {(users ?? []).map((u) => (
            <tr key={u.id}>
              <td style={s.td}>{u.display_name}</td>
              <td style={s.td}>{u.email}</td>
              <td style={{ ...s.td, fontFamily: "monospace", fontSize: 11 }}>{u.id}</td>
              <td style={s.td}>
                <select value={u.role} onChange={(e) => void changeRole(u.id, e.target.value as PlatformRole)}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </td>
              <td style={s.td}>
                <button
                  type="button"
                  style={s.buttonSecondary}
                  disabled={u.id === me?.id && u.is_active}
                  onClick={() => void toggleActive(u.id, u.is_active)}
                >
                  {u.is_active ? "Deactivate" : "Reactivate"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={s.mutedText}>
        There is no admin-initiated password reset — only self-service "forgot password" exists in this version (see
        docs/known-limitations.md).
      </p>
    </div>
  );
}

function AuditLogTab(): JSX.Element {
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getAuditLog()
      .then(setEntries)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load the audit log."));
  }, []);

  return (
    <div>
      {error && <p style={s.errorText}>{error}</p>}
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>When</th>
            <th style={s.th}>Action</th>
            <th style={s.th}>Target</th>
          </tr>
        </thead>
        <tbody>
          {(entries ?? []).map((e) => (
            <tr key={e.id}>
              <td style={s.td}>{new Date(e.created_at).toLocaleString()}</td>
              <td style={s.td}>{e.action}</td>
              <td style={s.td}>
                {e.target_type}: {e.target_id.slice(0, 8)}…
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreateOrgTab(): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      const org = await api.createOrg(name);
      navigate(`/orgs/${org.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create that organization.");
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} style={{ maxWidth: 360 }}>
      <label style={s.label}>
        Organization name
        <input style={s.input} required value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      {error && <p style={s.errorText}>{error}</p>}
      <button type="submit" style={s.buttonPrimary}>
        Create organization
      </button>
    </form>
  );
}

export function AdminPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState<"insights" | "users" | "audit" | "orgs">("insights");

  return (
    <div style={s.wide}>
      <h1 style={s.heading1}>Admin &amp; insights</h1>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button type="button" style={tab === "insights" ? s.buttonPrimary : s.buttonSecondary} onClick={() => setTab("insights")}>
          Insights
        </button>
        <button type="button" style={tab === "users" ? s.buttonPrimary : s.buttonSecondary} onClick={() => setTab("users")}>
          Users
        </button>
        {hasPermission("admin:system:manage") && (
          <>
            <button type="button" style={tab === "audit" ? s.buttonPrimary : s.buttonSecondary} onClick={() => setTab("audit")}>
              Audit log
            </button>
            <button type="button" style={tab === "orgs" ? s.buttonPrimary : s.buttonSecondary} onClick={() => setTab("orgs")}>
              Create organization
            </button>
          </>
        )}
      </div>
      {tab === "insights" && <AdminInsightsView />}
      {tab === "users" && <UsersTab />}
      {tab === "audit" && hasPermission("admin:system:manage") && <AuditLogTab />}
      {tab === "orgs" && hasPermission("admin:system:manage") && <CreateOrgTab />}
    </div>
  );
}
